// src/components/manager/shared/PostsFeed.jsx
import React, { useState } from 'react';
import { 
  Plus, 
  Calendar, 
  Globe, 
  Lock, 
  Users,
  MoreHorizontal, 
  Edit, 
  Trash2,
  Heart,
  MessageCircle,
  Share2,
  Download,
  Paperclip,
  Clock,
  CheckCircle,
  XCircle,
  Play,
  FileText,
  Image as ImageIcon
} from 'lucide-react';
import Masonry from 'react-masonry-css';
import { PostCard } from '../../institution/posts/PostCard';

export const PostsFeed = ({ 
  posts, 
  onCreatePost, 
  onEditPost, 
  onDeletePost, 
  onStatusChange,
  loading 
}) => {
  const [activeDropdown, setActiveDropdown] = useState(null);

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'public': return Globe;
      case 'internal': return Lock;
      case 'private': return Users;
      default: return Globe;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatDate = (date) => {
    const postDate = date?.toDate ? date.toDate() : new Date(date);
    return postDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTags = (tags) => {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags;
    if (typeof tags === 'string') return tags.split(',').map(t => t.trim()).filter(t => t);
    return [];
  };

  // ✅ FIXED: Check fileType field from Firestore
  const isImageFile = (file) => {
    if (!file) return false;
    // Check fileType field from Firestore
    if (file.fileType) return file.fileType.startsWith('image/');
    // Fallback to type field if it exists
    if (file.type) return file.type.startsWith('image/');
    // Check URL extension
    if (file.url) return /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(file.url);
    return false;
  };

  const isVideoFile = (file) => {
    if (!file) return false;
    if (file.fileType) return file.fileType.startsWith('video/');
    if (file.type) return file.type.startsWith('video/');
    if (file.url) return /\.(mp4|webm|ogg|mov)$/i.test(file.url);
    return false;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const breakpointColumns = {
    default: 2,
    1400: 2,
    1100: 2,
    700: 1
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Posts Feed</h3>
          <p className="text-sm text-gray-600">{posts.length} total posts</p>
        </div>
        <button
          onClick={onCreatePost}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create Post
        </button>
      </div>

      {/* Posts Masonry Grid */}
      {posts.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No posts yet</h3>
          <p className="text-gray-500 mb-6">Share your first update with the community</p>
          <button
            onClick={onCreatePost}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
          >
            Create Your First Post
          </button>
        </div>
      ) : (
        <Masonry
          breakpointCols={breakpointColumns}
          className="flex -ml-6 w-auto"
          columnClassName="pl-6 bg-clip-padding"
        >
          {posts.map(post => (
            <PostCard
              key={post.id}
              post={post}
              onEdit={() => onEditPost(post)}
              onDelete={() => onDeletePost(post.id)}
              onStatusChange={onStatusChange}
              getVisibilityIcon={getVisibilityIcon}
              getStatusColor={getStatusColor}
              formatDate={formatDate}
              getTags={getTags}
              isImageFile={isImageFile}
              isVideoFile={isVideoFile}
              formatFileSize={formatFileSize}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
          ))}
        </Masonry>
      )}
    </div>
  );
};


